import React, { useCallback, useEffect, useState } from 'react';
import { Check, Heart, Loader, X } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { authenticatedFetch } from '../utils/apiUtils';

const statusLabel = {
  pending: 'Ausstehend',
  booked: 'Termin vereinbart',
  not_booked: 'Nicht vereinbart',
};

const OfferInterestList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/offer-interests/`);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || 'Laden fehlgeschlagen');
      }
      const data = await response.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const updateStatus = async (id, appointmentStatus) => {
    setUpdatingId(id);
    setError('');
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/offer-interests/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ appointment_status: appointmentStatus }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || 'Aktualisierung fehlgeschlagen');
      }
      const updated = await response.json();
      setItems((current) => current.map((item) => (item.id === id ? updated : item)));
    } catch (err) {
      setError(err.message || 'Aktualisierung fehlgeschlagen');
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('de-CH');
  };

  return (
    <div>
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold flex items-center">
          <Heart className="mr-2 h-5 w-5 text-indigo-500" />
          Angebot-Interessenten
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Kunden mit Interesse an Kostenlose Fruhling Check. Bestätigen Sie, ob der Termin vereinbart wurde.
        </p>
      </div>
      <div className="p-6 space-y-4">

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center text-sm text-gray-500">
          <Loader className="h-4 w-4 mr-2 animate-spin" />
          Wird geladen...
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">Keine interessierten Kunden.</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Name</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Telefon</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Gemeldet</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Mitarbeiter</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Termin</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Aktionen</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">{item.customer_name}</td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">{item.customer_phone}</td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">{formatDate(item.created_at)}</td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">{item.created_by_username}</td>
                  <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">{statusLabel[item.appointment_status] || item.appointment_status}</td>
                  <td className="px-3 py-2 text-sm whitespace-nowrap">
                    {item.appointment_status === 'pending' ? (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={updatingId === item.id}
                          onClick={() => updateStatus(item.id, 'booked')}
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium text-white whitespace-nowrap bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                        >
                          <Check className="h-3.5 w-3.5 mr-1" />
                          Vereinbart
                        </button>
                        <button
                          type="button"
                          disabled={updatingId === item.id}
                          onClick={() => updateStatus(item.id, 'not_booked')}
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium text-white whitespace-nowrap bg-gray-500 hover:bg-gray-600 disabled:opacity-50"
                        >
                          <X className="h-3.5 w-3.5 mr-1" />
                          Nicht vereinbart
                        </button>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">Festgelegt</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </div>
  );
};

export default OfferInterestList;
